import { useMemo, useCallback, useEffect, useState } from "react";

import type { StackTemplate } from "@domains/application/types";
import type { DeploymentEvent } from "src/domains/application/hooks/useDeploymentEvents";
import type { PipelineStep as PipelineStateStep } from "src/domains/application/hooks/useDeploymentStream";
import type { PipelineRun } from "src/domains/application/hooks/usePipelineRuns";
import type { PipelineRunRow } from "src/domains/application/hooks/usePipelineRunsApi";
import type { VcsConnectionStatus } from "src/domains/application/hooks/useVcsConnectionStatus";
import { useApi } from "src/platform/hooks/useApi";

import { type TemplateServiceInfo } from "../pipeline-tab-helpers";
import type { StageInfo } from "../StagesSidebar";

import {
  computeAllCompleted,
  computeBuildFailed,
  computeEffectivePipelineState,
  computeGlobalFilteredPipelineState,
  computePipelineStatus,
  deriveStages,
} from "./pipeline-state-derive-helpers";
import {
  buildSyntheticPendingState,
  deriveLatestSagaId,
  filterEventsBySaga,
  filterPhaseRowsForDag,
  findSelectedApiRun,
  getScopedLogBatches,
  mapTemplateServices,
  mergeManifestServices,
  scopePipelineState,
} from "./pipeline-state-helpers";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface UsePipelineStateParams {
  alias: string;
  templateSlug?: string;
  status?: string;
  deployMode?: string;
  events: DeploymentEvent[];
  pipelineState: PipelineStateStep[];
  logBatches: Map<string, Map<string, Map<string, string[]>>>;
  selectedRunId: string | null;
  apiRuns: PipelineRunRow[] | undefined;
  runs: PipelineRun[];
  vcsStatus: VcsConnectionStatus;
  setSelectedRunId: (runId: string | null) => void;
}

export interface UsePipelineStateResult {
  templateServices: TemplateServiceInfo[] | undefined;
  latestSagaId: string | null;
  scopedLogBatches: Map<string, Map<string, string[]>>;
  scopedPipelineState: PipelineStateStep[];
  filteredPipelineState: PipelineStateStep[];
  filteredEvents: DeploymentEvent[];
  selectedApiRun: PipelineRunRow | null;
  syntheticPendingState: PipelineStateStep[];
  effectivePipelineState: PipelineStateStep[];
  globalFilteredPipelineState: PipelineStateStep[];
  pipelineStatus: string;
  buildFailed: boolean;
  allCompleted: boolean;
  stages: StageInfo[];
}

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * usePipelineState — pure derivations from raw pipeline inputs.
 *
 * Extracted from PipelineTabInner to keep the component focused on render.
 * All useMemo computations run in the same order as the original component
 * to satisfy React's rules of hooks. The pure derivation logic lives in
 * `pipeline-state-helpers.ts`; this file wires those functions into memoized
 * React state.
 */
export function usePipelineState(
  params: UsePipelineStateParams,
): UsePipelineStateResult {
  const {
    alias,
    templateSlug,
    status,
    events,
    pipelineState,
    logBatches,
    selectedRunId,
    apiRuns,
    runs,
    vcsStatus,
    setSelectedRunId,
  } = params;

  const { get } = useApi();

  // Fetch template services for service-level DAG nodes
  const [templateServices, setTemplateServices] = useState<
    TemplateServiceInfo[] | undefined
  >(undefined);
  useEffect(() => {
    if (!templateSlug) return;
    get<StackTemplate>(`/api/v1/templates/slug/${templateSlug}`, {
      showLoading: false,
      showErrorNotification: false,
    })
      .then((tmpl) => {
        if (tmpl?.services) setTemplateServices(mapTemplateServices(tmpl));
      })
      .catch(() => {
        /* Template fetch is best-effort for UI enrichment */
      });
  }, [templateSlug, get]);

  // Fetch assembled manifest services (includes observability-injected services
  // like prometheus/grafana) and merge them into the template services list so
  // the DAG group nodes show all services the stack actually has.
  useEffect(() => {
    get<{ spec?: { services?: Array<{ name: string; type: string }> } }>(
      `/api/v1/applications/${alias}/manifest`,
      { showLoading: false, showErrorNotification: false },
    )
      .then((manifest) => {
        const services = manifest?.spec?.services;
        if (!services || services.length === 0) return;
        setTemplateServices((prev) => mergeManifestServices(prev, services));
      })
      .catch(() => {
        // Best-effort enrichment; raw template services already set
      });
  }, [alias, get]);

  // Auto-advance to a new active run (pending/in_progress/queued) when it
  // arrives — but do NOT auto-select a run on first load. The user should
  // see the runs list first and click to select one.
  const isFreshRun = useCallback(
    (sagaId: string, status: string) =>
      selectedRunId !== null &&
      selectedRunId !== sagaId &&
      (status === "pending" || status === "in_progress" || status === "queued"),
    [selectedRunId],
  );

  useEffect(() => {
    if (selectedRunId === null) return; // no auto-select on first visit
    const newest = runs[0] ?? apiRuns?.[0];
    if (!newest) return;
    if (isFreshRun(newest.sagaId, newest.status)) {
      setSelectedRunId(newest.sagaId);
    }
  }, [runs, apiRuns, selectedRunId, isFreshRun, setSelectedRunId]);

  // Scope pipeline data to a specific sagaId (run). When no run is selected,
  // use the latest sagaId from pipelineState so the DAG always reflects a
  // single run — never a mix of stale and current states. (See
  // deriveLatestSagaId for why phase-level rows are excluded.)
  const latestSagaId = useMemo(
    () => deriveLatestSagaId(pipelineState),
    [pipelineState],
  );

  // Scope log batches to the selected run (or latest run)
  const scopedLogBatches = useMemo(
    () => getScopedLogBatches(logBatches, selectedRunId ?? latestSagaId),
    [logBatches, selectedRunId, latestSagaId],
  );

  const scopedPipelineState = useMemo(
    () => scopePipelineState(pipelineState, selectedRunId ?? latestSagaId),
    [pipelineState, selectedRunId, latestSagaId],
  );

  // Filter out phase-level rows (serviceName === "") for build/deploy/vcs,
  // except "failed" phase rows (see filterPhaseRowsForDag). VCS rows are
  // irrelevant for the DAG (sourced from VcsConnection API).
  const filteredPipelineState = useMemo(
    () => filterPhaseRowsForDag(scopedPipelineState),
    [scopedPipelineState],
  );

  // When a run is selected, filter events to that sagaId; otherwise use all events
  const filteredEvents = useMemo(
    () => filterEventsBySaga(events, selectedRunId),
    [events, selectedRunId],
  );

  // When a run is selected (e.g. a freshly triggered "queued" run) but
  // filteredPipelineState is empty, generate synthetic "pending" steps so the
  // DAG shows placeholder nodes (see buildSyntheticPendingState).
  const selectedApiRun = useMemo(
    () => findSelectedApiRun(selectedRunId, apiRuns),
    [selectedRunId, apiRuns],
  );

  const syntheticPendingState = useMemo<PipelineStateStep[]>(
    () =>
      buildSyntheticPendingState({
        selectedRunId,
        selectedApiRun,
        filteredPipelineState,
        templateServices,
        alias,
        pipelineState,
      }),
    [
      selectedRunId,
      selectedApiRun,
      filteredPipelineState,
      templateServices,
      alias,
      pipelineState,
    ],
  );

  // Resolve the effective pipeline state used by the DAG (per-service rows,
  // then synthetic pending, then phase-level fallback). See
  // computeEffectivePipelineState.
  const effectivePipelineState = useMemo(
    () =>
      computeEffectivePipelineState({
        filteredPipelineState,
        syntheticPendingState,
        pipelineState,
        selectedRunId,
        latestSagaId,
      }),
    [
      filteredPipelineState,
      syntheticPendingState,
      pipelineState,
      selectedRunId,
      latestSagaId,
    ],
  );

  // Global pipeline state (not scoped to a single run), excluding phase-level
  // rows, scoped to the latest sagaId. Build failure is a global condition
  // that shouldn't disappear when viewing a different run's deploy status.
  // See computeGlobalFilteredPipelineState.
  const globalFilteredPipelineState = useMemo(
    () =>
      computeGlobalFilteredPipelineState(
        pipelineState,
        selectedRunId ?? latestSagaId,
      ),
    [pipelineState, selectedRunId, latestSagaId],
  );

  // Compute pipeline status from pipelineState (materialized view) when
  // available, fallback to events. VCS is decoupled. See computePipelineStatus.
  const pipelineStatus = useMemo(
    () =>
      computePipelineStatus({
        pipelineState,
        globalFilteredPipelineState,
        events,
        selectedApiRun,
      }),
    [pipelineState, globalFilteredPipelineState, events, selectedApiRun],
  );

  const buildFailed = useMemo(
    () => computeBuildFailed(globalFilteredPipelineState, events),
    [globalFilteredPipelineState, events],
  );

  // All completed — show redeploy option
  const allCompleted = useMemo(
    () => computeAllCompleted(globalFilteredPipelineState),
    [globalFilteredPipelineState],
  );

  // Derive stages for the StagesSidebar (see deriveStages).
  const stages = useMemo<StageInfo[]>(
    () =>
      deriveStages({
        globalFilteredPipelineState,
        pipelineState,
        selectedApiRun,
        selectedRunId,
        latestSagaId,
        status,
        vcsStatus,
      }),
    [
      globalFilteredPipelineState,
      pipelineState,
      selectedApiRun,
      selectedRunId,
      latestSagaId,
      status,
      vcsStatus,
    ],
  );

  return {
    templateServices,
    latestSagaId,
    scopedLogBatches,
    scopedPipelineState,
    filteredPipelineState,
    filteredEvents,
    selectedApiRun,
    syntheticPendingState,
    effectivePipelineState,
    globalFilteredPipelineState,
    pipelineStatus,
    buildFailed,
    allCompleted,
    stages,
  };
}
